// "spin" interaction — rotating-rack behaviour, extracted from the legacy
// spinner-rack-anchor.js builder for the DECISION-050 single render path.
//
// The single path instances the spinner GLB (frame) + resolves the ordered baked
// SLOT_RACK_* nodes, then calls this to: parent the comics under a `spinRoot` group
// (at each slot's frame-LOCAL transform) and install `stage.spinnerRack` so the stage's
// existing per-frame spin physics (rotation += velocity·dt, exponential friction),
// arrow-key impulse, hover, and hero lift/return all drive them unchanged.
//
// Stateful + stage-coupled by nature (single rack per shop in v1, on `stage.spinnerRack`).
// The pure layout/seating is headless-tested elsewhere; the rack *motion* is browser-
// verified (rides the walkthrough) — this module only re-homes the SETUP the stage reads.

import * as THREE from "../../../../vendor/three.module.js";
import { registerInteraction } from "./index.js";

const FRICTION = 4;   // exponential damping (matches the legacy builder + stage updater)

/**
 * @param {object} ctx
 * @param {THREE.Object3D} ctx.frame       the instanced spinner GLB (rack geometry)
 * @param {THREE.Object3D[]} ctx.slotNodes the ordered baked SLOT_RACK_* nodes (poses)
 * @param {Array} ctx.products             curated comics for this socket (RACK fill)
 * @param {object} ctx.stage               the runtime stage (gets `stage.spinnerRack`)
 * @param {(product)=>THREE.Mesh} ctx.buildComic  builds a bare comic mesh (positioned here)
 * @returns {number} comics seated
 */
function wireSpin(ctx) {
	const { frame, slotNodes, products, stage, buildComic, spinNodeName = "SPIN_ROOT" } = ctx;
	if (!frame || !stage || typeof buildComic !== "function") return 0;

	// Coord ruling 2026-06-30: rotate the manifest-declared SPIN_ROOT node, NOT the whole
	// frame — the base stays static. The 36 baked SLOT_RACK_* empties are already children
	// of SPIN_ROOT (so they rotate with it); we parent the comics under it too. The frame
	// (with its static base + SPIN_ROOT) was already instanced + placed at the anchor.
	const spinRoot = frame.getObjectByName(spinNodeName);
	if (!spinRoot) {
		console.warn(`[spin] '${spinNodeName}' node not found in fixture GLB — spinner not wired`);
		return 0;
	}
	frame.updateWorldMatrix(true, true);
	const spinInv = new THREE.Matrix4().copy(spinRoot.matrixWorld).invert();

	const comics = [];
	const n = Math.min(products.length, slotNodes.length);
	const m = new THREE.Matrix4(), pos = new THREE.Vector3(), quat = new THREE.Quaternion(), scl = new THREE.Vector3();
	for (let i = 0; i < n; i++) {
		const node = slotNodes[i];
		node.updateWorldMatrix(true, false);
		m.multiplyMatrices(spinInv, node.matrixWorld);    // slot pose in SPIN_ROOT-local space
		m.decompose(pos, quat, scl);
		const mesh = buildComic(products[i]);
		mesh.position.copy(pos);
		mesh.quaternion.copy(quat);
		mesh.userData.isSpinnerComic = true;
		mesh.userData.interactable = {
			kind: "spinner-comic",   // stage's interactable dispatcher (_interactable-kinds/spinner-rack.js)
			productIndex: i,
			product: products[i],
			describe: () => ({ kind: "spinner-comic", productId: products[i]?.id, idx: i }),
		};
		spinRoot.add(mesh);
		comics.push({ mesh, product: products[i], slotLocalPos: pos.clone(), slotQuat: quat.clone(), side: 0, row: i });
	}

	// The shape the stage's spin updater + hero/hover read (matches legacy builder).
	// worldGroup = the static frame (base + SPIN_ROOT); spinRoot = the rotating node.
	stage.spinnerRack = {
		worldGroup: frame, spinRoot,
		rotation: spinRoot.rotation.y, velocity: 0, friction: FRICTION,
		comics, hoveredComic: null, heroEntry: null, state: "none",
	};
	return comics.length;
}

registerInteraction("spin", wireSpin);
export { wireSpin };
